import { Box, Typography, useMediaQuery, TextField, IconButton, Button, ButtonGroup, Select, MenuItem, Autocomplete } from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import SitePoleFilterDialog from './sitePoleFilterDialog';
import useDebounce from '../../hooks/useDebounce';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Iconify from '../Iconify';
import axios from 'axios';
import '../../css/machines/MachineFiltration.css';

// Static data for machine status
const machine_status = [
    { id: 'Valide', name: 'Valide' },
    { id: 'AvecReparation', name: 'Avec Reparation' },
    { id: 'NonConforme', name: 'Non Conforme' },
    { id: 'Indéfini', name: 'Indéfini' },
];

const MachineFiltration = ({ filtersEmpty, filters, setFilters, totalMachines, regions, sites, poles, metiers, brands, types, status, machineNoteCount, totalPoleMachines, totalPoleMetiers, selectedDisplayMode, setSelectedDisplayMode, filteredMachinesCount, machinesFetched }) => {

    const { t } = useTranslation();
    const theme = useTheme();
    const authenticatedUser = useSelector(state => state.AuthenticatedUser);

    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const containerRef = useRef(null); // To reference the filtration container

    const [showResponsiveFilter, setShowResponsiveFilter] = useState(false);
    const [openSitePoleFilterDialog, setOpenSitePoleFilterDialog] = useState(false);
    const [showArrows, setShowArrows] = useState(false);
    const [search, setSearch] = useState(filters.debouncedSearch || '');
    const debouncedSearch = useDebounce(search, 1000);

    useEffect(() => {
        setFilters(prev => ({ ...prev, debouncedSearch }));
    }, [debouncedSearch, setFilters]);

    const handleGridMode = useCallback(() => {
        setSelectedDisplayMode('grid');
    }, [setSelectedDisplayMode]);

    const handleListMode = useCallback(() => {
        setSelectedDisplayMode('list');
    }, [setSelectedDisplayMode]);


    // Show/hide arrows based on overflow
    useEffect(() => {
        const container = containerRef.current;

        const checkOverflow = () => {
            if (!container) return;
            setShowArrows(container.scrollWidth > container.clientWidth);
        };

        const resizeObserver = new ResizeObserver(checkOverflow);
        container && resizeObserver.observe(container);

        checkOverflow();
        window.addEventListener('resize', checkOverflow);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [isDesktop]);

    const handleScrollFilterBar = useCallback((direction) => {
        const scrollAmount = 250;
        if (containerRef.current) {
            containerRef.current.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    }, []);

    const handleChangeRegion = (region) => {
        setFilters((prev) => ({ ...prev, selectedRegion: region, selectedSite: '', selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' }));
    }

    const handleChangeMetier = (metier) => {
        setFilters((prev) => ({ ...prev, selectedMetier: metier, selectedStatus: '', selectedType: '', selectedMark: '' }));
    }

    const handleChangeStatus = (status) => {
        setFilters((prev) => ({ ...prev, selectedStatus: status }));
    }

    const handleChangeMark = (mark) => {
        setFilters((prev) => ({ ...prev, selectedMark: mark }));
    }

    const handleChangeType = (type) => {
        setFilters((prev) => ({ ...prev, selectedType: type }));
    }

    const handleResetFilters = useCallback(() => {
        setFilters({
            search: '', selectedRegion: '', selectedSite: '', selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '',
            internal_codification: '', model: '', dimensions: '', control_date: '', next_control_date: '', commissioning_date: '',
            contract_start_date: '', contract_end_date: '', electric_power: '', intensity: '', purchase_date: '', manufacture_date: '',
            purchase_price: '', serial_number: '', entreprise: '', selectedArchiveStatus: window.location.pathname.includes('machines_archivees') ? 'Archived' : 'Unarchived'
        });
        setSearch('');
    }, [setFilters]);

    const handleExportMachines = async (format) => {
        try {
            const response = await axios.get('machine/exportMachines', {
                params: { filters, sortBy: {}, format }, // Pass the format (Excel or PDF)
                responseType: 'blob', // Handle binary data
            });

            if (response.status === 200) {
                // Extract the filename from the Content-Disposition header
                const contentDisposition = response.headers['content-disposition'];
                let fileName = 'machines.pdf'; // Default filename
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+)"/);
                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }

                // Create a URL for the file
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName); // Use the extracted filename
                document.body.appendChild(link);
                link.click();

                // Clean up the URL object
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error exporting machines:', error);
            CustomErrorLogger(error, 'export machines in contracts page');
        }
    };

    return (
        <Box>
            <Box className='MachineFiltration_main_Box'>
                {isDesktop &&
                    <Box className='MachineFiltration_fix_mode_box'>
                        <Box className='MachineFiltration_search_Box'>
                            <TextField
                                size="small"
                                value={search}
                                placeholder='Rechercher'
                                onChange={(e) => setSearch(e.target.value)}
                                variant="outlined"
                                className='MachineFiltration_search_field'
                                InputProps={{
                                    startAdornment: <Iconify icon={"bx:bx-search"} className='MachineFiltration_search_icon' />,
                                    sx: {
                                        backgroundColor: '#fff',
                                        height: '36px',
                                        fontFamily: 'Mulish',
                                        color: '#201E1E',
                                        fontSize: '14px',
                                        fontWeight: 400,
                                    },
                                }}
                            />
                            <Typography className='MachineFiltration_clearAll' onClick={handleResetFilters}>Re-initialiser</Typography>
                        </Box>
                        <Box className='MachineFiltration_search_Box'>
                            {machineNoteCount > 0 &&
                                <Typography className={filters.machineNote ? 'MachineFiltration_with_note_filter' : 'MachineFiltration_without_note_filter'} onClick={() => setFilters({ ...filters, machineNote: !filters.machineNote })}>{machineNoteCount} machines avec remarques</Typography>
                            }
                            {!window.location.pathname.includes('machines_archivees') &&
                                <Select
                                    className='MachineFiltration_select_metier'
                                    size="small"
                                    value=''
                                    onChange={(e) => handleExportMachines(e.target.value)}
                                    startAdornment={<Iconify icon={"material-symbols:file-export-outline-rounded"} className='MachineFiltration_select_metier_icon' />}
                                    displayEmpty
                                    renderValue={() => {
                                        return <span className='MachineFiltration_select_placeholder'>{t('Exporter')}</span>;
                                    }}
                                >
                                    <MenuItem className='MachineFiltration_select_metier_item' value="PDF">PDF</MenuItem>
                                    <MenuItem className='MachineFiltration_select_metier_item' value="Excel">Excel</MenuItem>
                                </Select>
                            }
                            {isDesktop &&
                                <ButtonGroup variant="outlined" >
                                    <Button
                                        className={selectedDisplayMode === 'grid' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                        onClick={handleGridMode}
                                    >
                                        <Iconify icon={"gg:menu-grid-r"} />
                                    </Button>
                                    <Button
                                        className={selectedDisplayMode === 'list' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                        onClick={handleListMode}
                                    >
                                        <Iconify icon={"ph:list"} />
                                    </Button>
                                </ButtonGroup>
                            }

                            <Typography className='MachineFiltration_total_number'>
                                {machinesFetched} affichés
                                {!filtersEmpty && ` / ${filteredMachinesCount} filtrés`}
                                {` / ${totalMachines} au total`}
                            </Typography>
                        </Box>
                    </Box>
                }
                <Box className='MachineFiltration_wrapper'>
                    {(isDesktop || showResponsiveFilter) &&
                        <Box ref={containerRef} className='MachineFiltration_container'>
                            {!isDesktop &&
                                <TextField
                                    size="small"
                                    value={search}
                                    placeholder='Rechercher'
                                    onChange={(e) => setSearch(e.target.value)}
                                    variant="outlined"
                                    className='MachineFiltration_search_field'
                                    InputProps={{
                                        startAdornment: <Iconify icon={"bx:bx-search"} className='MachineFiltration_search_icon' />,
                                        sx: {
                                            backgroundColor: '#fff',
                                            height: '36px',
                                            fontFamily: 'Mulish',
                                            color: '#201E1E',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                        },
                                    }}
                                />
                            }
                            {selectedDisplayMode === 'grid' && regions?.length > 1 &&
                                <Select
                                    className='MachineFiltration_select_metier'
                                    size="small"
                                    value={filters.selectedRegion}
                                    onChange={(e) => handleChangeRegion(e.target.value)}
                                    startAdornment={<Iconify icon={"oui:vis-map-region"} className='MachineFiltration_select_metier_icon' />}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <span className='MachineFiltration_select_placeholder'>{t('Region')}</span>;
                                        }
                                        return selected.name;
                                    }}
                                >
                                    <MenuItem className='MachineFiltration_select_metier_item' value="">{t('Tout')}</MenuItem>
                                    {regions?.map((region) => (
                                        <MenuItem className='MachineFiltration_select_metier_item' value={region}>{region.name}</MenuItem>
                                    ))}
                                </Select>
                            }

                            {(selectedDisplayMode === 'grid' && !(authenticatedUser?.user_site_ids?.length === 1 && authenticatedUser?.user_pole_ids?.length === 1)) &&
                                <Box className='MachineFiltration_sites_Box' onClick={() => setOpenSitePoleFilterDialog(true)}>
                                    <Iconify icon={"material-symbols-light:location-city"} />
                                    <Typography>{filters.selectedSite ? filters.selectedSite.name + ' / ' + (filters.selectedPole?.name ?? 'Sélectionner le pôle') : t('machineFilterSitesLabel')}</Typography>
                                    <Iconify icon={"material-symbols-light:cards-outline"} />
                                </Box>
                            }

                            {(selectedDisplayMode === 'grid' && authenticatedUser?.user_metier_ids?.length > 1) &&
                                <Autocomplete
                                    className='MachineFiltration_select_metier'
                                    size="small"
                                    value={filters.selectedMetier}
                                    onChange={(event, newValue) => handleChangeMetier(newValue)}
                                    options={metiers}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t('Metier')}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <Iconify icon={"uil:bag-alt"} className='MachineFiltration_select_metier_icon' />
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option, { selected }) => (
                                        <MenuItem
                                            {...props}
                                            selected={selected}
                                            className='MachineFiltration_select_metier_item'
                                        >
                                            {option.name}
                                        </MenuItem>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    noOptionsText={t('No options')}
                                />
                            }

                            {selectedDisplayMode === 'grid' &&
                                <Select
                                    className='MachineFiltration_select_metier'
                                    size="small"
                                    value={filters.selectedStatus}
                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                    startAdornment={<Iconify icon={"pajamas:status"} className='MachineFiltration_select_metier_icon' />}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <span className='MachineFiltration_select_placeholder'>{t('Statut')}</span>;
                                        }
                                        return selected.name;
                                    }}
                                >
                                    <MenuItem className='MachineFiltration_select_metier_item' value="">{t('Tout')}</MenuItem>
                                    {machine_status.filter(ms => status?.includes(ms.id))?.map((status) => (
                                        <MenuItem className='MachineFiltration_select_metier_item' value={status}>{status.name}</MenuItem>
                                    ))}
                                </Select>
                            }

                            {selectedDisplayMode === 'grid' &&
                                <Select
                                    className='MachineFiltration_select_metier'
                                    size="small"
                                    value={filters.selectedMark}
                                    onChange={(e) => handleChangeMark(e.target.value)}
                                    startAdornment={<Iconify icon={"pajamas:status"} className='MachineFiltration_select_metier_icon' />}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <span className='MachineFiltration_select_placeholder'>{t('Marque')}</span>;
                                        }
                                        return selected.name;
                                    }}
                                >
                                    <MenuItem className='MachineFiltration_select_metier_item' value="">{t('Tout')}</MenuItem>
                                    {brands?.map((brand) => (
                                        <MenuItem className='MachineFiltration_select_metier_item' value={brand}>{brand.name}</MenuItem>
                                    ))}
                                </Select>
                            }

                            {selectedDisplayMode === 'grid' &&
                                <ButtonGroup variant="outlined">
                                    <Button
                                        className={filters.selectedType === '' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                        onClick={() => handleChangeType('')}
                                    >
                                        {t('Tout')}
                                    </Button>
                                    {types.includes('Fixe') &&
                                        <Button
                                            className={filters.selectedType === 'Fixe' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                            onClick={() => handleChangeType('Fixe')}
                                        >
                                            {t('Fixe')}
                                        </Button>
                                    }
                                    {types.includes('Portative') &&
                                        <Button
                                            className={filters.selectedType === 'Portative' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                            onClick={() => handleChangeType('Portative')}
                                        >
                                            {t('Portative')}
                                        </Button>
                                    }
                                    {types.includes('Indéfini') &&
                                        <Button
                                            className={filters.selectedType === 'Indéfini' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                            onClick={() => handleChangeType('Indéfini')}
                                        >
                                            {t('Indéfini')}
                                        </Button>
                                    }
                                </ButtonGroup>
                            }
                        </Box>
                    }

                    {showArrows && (
                        <Box className='MachineFiltration_scroll_icon_box'>
                            <IconButton onClick={() => handleScrollFilterBar('left')}>
                                <Iconify icon="ic:round-arrow-back-ios" className='MachineFiltration_scroll_icon' />
                            </IconButton>
                            <IconButton onClick={() => handleScrollFilterBar('right')}>
                                <Iconify icon="ic:round-arrow-forward-ios" className='MachineFiltration_scroll_icon' />
                            </IconButton>
                        </Box>
                    )}

                    {!isDesktop &&
                        <Box className='MachineFiltration_filter_icon_ButtonGroup_box'>

                            <Box className='MachineFiltration_filter_icon_export_box'>
                                <Iconify
                                    className='MachineFiltration_filter_icon'
                                    icon={"cil:filter"}
                                    backgroundColor={showResponsiveFilter ? '#182E45' : '#fffff'}
                                    color={showResponsiveFilter ? '#ffffff' : '#201E1E'}
                                    onClick={() => setShowResponsiveFilter(!showResponsiveFilter)}
                                />
                                {machineNoteCount > 0 &&
                                    <Typography className={filters.machineNote ? 'MachineFiltration_with_note_filter' : 'MachineFiltration_without_note_filter'} onClick={() => setFilters({ ...filters, machineNote: !filters.machineNote })}>{machineNoteCount} machines avec remarques</Typography>
                                }
                                {!window.location.pathname.includes('machines_archivees') &&
                                    <Select
                                        sx={{ backgroundColor: 'white' }}
                                        size="small"
                                        value=''
                                        onChange={(e) => handleExportMachines(e.target.value)}
                                        startAdornment={<Iconify icon={"material-symbols:file-export-outline-rounded"} className='MachineFiltration_select_metier_icon' />}
                                        displayEmpty
                                        renderValue={() => {
                                            return <span className='MachineFiltration_select_placeholder'>{t('Exporter')}</span>;
                                        }}
                                    >
                                        <MenuItem className='MachineFiltration_select_metier_item' value="PDF">PDF</MenuItem>
                                        <MenuItem className='MachineFiltration_select_metier_item' value="Excel">Excel</MenuItem>
                                    </Select>
                                }
                            </Box>

                            <ButtonGroup variant="outlined">
                                <Button
                                    className={selectedDisplayMode === 'grid' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                    onClick={handleGridMode}
                                >
                                    <Iconify icon={"gg:menu-grid-r"} />
                                </Button>
                                <Button
                                    className={selectedDisplayMode === 'list' ? 'MachineFiltration_options_selected_button' : 'MachineFiltration_options_button'}
                                    onClick={handleListMode}
                                >
                                    <Iconify icon={"ph:list"} />
                                </Button>
                            </ButtonGroup>

                            <Typography className='MachineFiltration_clearAll' onClick={handleResetFilters}>Re-initialiser</Typography>

                            <Typography className='MachineFiltration_total_number'>
                                {machinesFetched} affichés
                                {!filtersEmpty && ` / ${filteredMachinesCount} filtrés`}
                                {` / ${totalMachines} au total`}
                            </Typography>
                        </Box>
                    }
                </Box>
            </Box>

            {openSitePoleFilterDialog &&
                <SitePoleFilterDialog
                    sites={sites}
                    poles={poles}
                    totalPoleMachines={totalPoleMachines}
                    totalPoleMetiers={totalPoleMetiers}
                    filters={filters}
                    setFilters={setFilters}
                    openSitePoleFilterDialog={openSitePoleFilterDialog}
                    setOpenSitePoleFilterDialog={setOpenSitePoleFilterDialog}
                />
            }

        </Box>
    );
};

export default MachineFiltration;